import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import type { Question } from '@/types/form'
import { ChoiceBatchEditor } from './ChoiceBatchEditor'

interface MultipleChoiceSettingsProps {
  question: Question
  onQuestionChange: (patch: Partial<Question>) => void
  onChange: (patch: Partial<Question['settings']>) => void
}

export function MultipleChoiceSettings({
  question,
  onQuestionChange,
  onChange,
}: MultipleChoiceSettingsProps) {
  const isPictureChoice = question.type === 'PICTURE_CHOICE'
  const isRanking = question.type === 'RANKING'
  const canUseChoiceFlowToggles = !isRanking

  return (
    <div className="flex flex-col gap-4">
      <ChoiceBatchEditor
        question={question}
        label={isRanking ? '排序項目' : '選項'}
        mode={isPictureChoice ? 'picture' : 'standard'}
        onQuestionChange={onQuestionChange}
        onSettingsChange={onChange}
      />

      {canUseChoiceFlowToggles && (
        <>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span>允許多選</span>
            <Switch
              aria-label="允許多選"
              checked={Boolean(question.settings.allowMultiple)}
              onCheckedChange={(allowMultiple) => onChange({ allowMultiple })}
            />
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span>顯示「其他」</span>
            <Switch
              aria-label="顯示其他選項"
              checked={Boolean(question.settings.showOther)}
              onCheckedChange={(showOther) => onChange({ showOther })}
            />
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span>選擇後自動下一題</span>
            <Switch
              aria-label="選擇後自動下一題"
              checked={Boolean(question.settings.autoAdvance)}
              onCheckedChange={(autoAdvance) => onChange({ autoAdvance })}
            />
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span>隨機排列選項</span>
            <Switch
              aria-label="隨機排列選項"
              checked={Boolean(question.settings.randomize)}
              onCheckedChange={(randomize) => onChange({ randomize })}
            />
          </div>
          {question.settings.allowMultiple && (
            <div className="flex flex-col gap-2">
              <span className="text-sm text-slate-600">
                至少選擇 {question.settings.minSelected ?? 1} 項，最多選擇{' '}
                {question.settings.maxSelected ?? Math.min(3, question.choices.length || 1)} 項
              </span>
              <Slider
                value={[Number(question.settings.minSelected ?? 1)]}
                min={1}
                max={Math.max(question.choices.length, 1)}
                step={1}
                onValueChange={([minSelected]) => onChange({ minSelected })}
              />
              <Label>最多選擇數：{question.settings.maxSelected ?? 3}</Label>
              <Slider
                value={[Number(question.settings.maxSelected ?? 3)]}
                min={1}
                max={Math.max(question.choices.length, 1)}
                step={1}
                onValueChange={([maxSelected]) => onChange({ maxSelected })}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
